import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";

/**
 * usePageExit
 *
 * Returns a function that animates the current service page out (fade + slide-down)
 * and then navigates to the homepage with a target section to scroll to.
 *
 * Usage:
 *   const exitTo = usePageExit();
 *   exitTo("realisations") // closes page, scrolls to #realisations on home
 *   exitTo("contact")      // closes page, scrolls to #contact on home
 *
 * The HomePage reads `location.state.scrollTo` and performs the smooth scroll.
 */
export function usePageExit() {
  const navigate = useNavigate();

  return useCallback(
    (sectionId, options = {}) => {
      const {
        rootSelector = "[data-testid$='-page']",
        duration = 0.6,
      } = options;

      const root = document.querySelector(rootSelector);
      const go = () => navigate("/", { state: { scrollTo: sectionId } });

      if (!root) {
        go();
        return;
      }
      gsap.to(root, {
        opacity: 0,
        y: 30,
        duration,
        ease: "power2.in",
        onComplete: go,
      });
    },
    [navigate]
  );
}
